import { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
    Button, Input, Select, InputNumber, Modal, Form, Empty, message, Drawer,
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Column } from '@ant-design/plots'
import { requestWorkouts, createWorkout, assertDaemonCompatible, checkDaemonCompatibility } from './store/store'
import { setSelectedWorkout, resetCreateStatus } from './store/workouts'
import { getLocalStorageValue, setLocalStorageValue } from './store/localstorage'
import { showDaemonBlockModal } from './DaemonBlockUi'
import {
    ZONES, FAVORITES_KEY,
    filterWorkouts, sortWorkouts, formatZoneSummary,
    workoutTotalSeconds, toggleFavoriteIds, formatDuration,
} from './workoutLogic'
import { expandDesignerRows, FTP_KEY } from './playerLogic'
import './css/workouts.css'

const TABS = [
    { key: 'all', label: 'Все' },
    { key: 'favorites', label: 'Избранные' },
    { key: 'mine', label: 'Мои' },
]

const ZONE_OPTIONS = ZONES.map(([value, label]) => ({ value, label }))

const DURATION_OPTIONS = [
    { value: 'lt30', label: 'до 30 мин' },
    { value: '30to60', label: '30–60 мин' },
    { value: 'gt60', label: 'более 60 мин' },
]

const SORT_OPTIONS = [
    { value: 'default', label: 'По умолчанию' },
    { value: 'duration', label: 'По длительности' },
    { value: 'name', label: 'По названию' },
    { value: 'author', label: 'По автору' },
    { value: 'zone', label: 'По времени в зоне' },
]

function WorkoutProfileThumbnail({ points }) {
    const duration = points && points.length ? Math.max(...points.map(p => p.t), 1) : 1
    const values = points || []
    const maxV = Math.max(1, ...values.map(p => p.v))
    const minV = 0
    const range = Math.max(1, maxV - minV)

    const toX = t => (t / duration) * 100
    const toY = v => 100 - ((v - minV) / range) * 92 - 4

    const line = values.map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(p.t).toFixed(2)} ${toY(p.v).toFixed(2)}`).join(' ')
    const area = values.length
        ? `${line} L 100 100 L 0 100 Z`
        : ''

    return (
        <svg className="workout-profile" viewBox="0 0 100 56" preserveAspectRatio="none"
            role="img" aria-label="Профиль мощности тренировки">
            <line className="workout-profile__baseline" x1="0" y1="96" x2="100" y2="96" />
            {area && <path className="workout-profile__area" d={area} />}
            {line && <path className="workout-profile__line" d={line} />}
        </svg>
    )
}

function pluralMinutes(n) {
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return `${n} минута`
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} минуты`
    return `${n} минут`
}

function WorkoutPreview({ workout, onStart }) {
    const data = ZONES
        .map(([key, label]) => {
            const p = (workout.properties || []).find(x => x.name === `time_in_zone: ${key}`)
            return { zone: label, minutes: Math.round(((p && p.val_int) || 0) / 60) }
        })
        .filter(d => d.minutes > 0)

    const chartConfig = {
        data,
        xField: 'zone',
        yField: 'minutes',
        height: 220,
        color: '#29a9df',
        theme: 'classicDark',
        interactions: [{ type: 'active-region' }],
        tooltip: {
            items: [
                { name: 'Время', field: 'minutes', valueFormatter: d => pluralMinutes(d) },
            ],
        },
        axis: {
            x: {
                labelStyle: { fill: '#e8e8e8' },
                tick: false,
            },
            y: {
                labelStyle: { fill: '#a7a7a7' },
                grid: true,
                gridStroke: '#3a3a3a',
                gridLineDash: [3, 3],
            },
        },
    }

    return (
        <section className="workouts-preview" aria-label="Предпросмотр тренировки">
            <div className="workouts-preview__header">
                <div>
                    <h2>{workout.name || 'Без названия'}</h2>
                    <p>{workout.author || 'Автор не указан'}</p>
                </div>
                <Button type="primary" onClick={() => onStart(workout)}>Начать</Button>
            </div>
            <div className="workouts-preview__zone-chart">
                {data.length ? <Column {...chartConfig} /> : <Empty description="Нет данных по зонам" />}
            </div>
        </section>
    )
}

function DesignerModal({ open, onClose, onCreated }) {
    const [form] = Form.useForm()
    const dispatch = useDispatch()
    const createStatus = useSelector(state => state.workouts.createStatus)

    useEffect(() => {
        if (open) {
            dispatch(resetCreateStatus())
            form.setFieldsValue({
                author: '',
                name: '',
                rows: [{ target_pct: 100, time_s: 60, repeats: 1 }],
            })
        }
    }, [open, form, dispatch])

    const handleOk = async () => {
        const values = await form.validateFields()
        const points = expandDesignerRows(values.rows)
        try {
            await createWorkout({
                author: values.author || '—',
                name: values.name,
                points,
                origin: {
                    source: 'web-designer',
                    key: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
                },
            })
            message.success('Тренировка создана')
            onCreated()
            onClose()
        } catch (e) {
            const msg = (e && e.code === 'Duplicate')
                ? 'Такая тренировка уже существует'
                : ((e && e.message) || 'Не удалось создать тренировку')
            message.error(msg)
        }
    }

    return (
        <Modal
            title="Создать тренировку"
            visible={open}
            onOk={handleOk}
            onCancel={onClose}
            okText="Создать"
            cancelText="Отмена"
            confirmLoading={createStatus === 'loading'}
            destroyOnClose
        >
            <Form form={form} layout="vertical" name="designer">
                <Form.Item name="author" label="Автор">
                    <Input placeholder="Автор" />
                </Form.Item>
                <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Укажите название' }]}>
                    <Input placeholder="Название тренировки" />
                </Form.Item>
                <Form.List name="rows">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, index) => (
                                <div className="designer-row" key={field.key}>
                                    <Form.Item name={[field.name, 'target_pct']} rules={[{ required: true, message: 'Цель' }]}
                                        label={index === 0 ? 'Цель, % FTP' : ''}>
                                        <InputNumber min={0} max={130} style={{ width: '100%' }} placeholder="%" />
                                    </Form.Item>
                                    <Form.Item name={[field.name, 'time_s']} rules={[{ required: true, message: 'Время' }]}
                                        label={index === 0 ? 'Время, с' : ''}>
                                        <InputNumber min={1} style={{ width: '100%' }} placeholder="с" />
                                    </Form.Item>
                                    <Form.Item name={[field.name, 'repeats']} rules={[{ required: true, message: 'Повторы' }]}
                                        label={index === 0 ? 'Повторы' : ''}>
                                        <InputNumber min={1} style={{ width: '100%' }} placeholder="раз" />
                                    </Form.Item>
                                    <Form.Item label=" " colon={false} className="designer-row__remove">
                                        <Button icon={<DeleteOutlined />} onClick={() => remove(field.name)} aria-label="Удалить интервал" />
                                    </Form.Item>
                                </div>
                            ))}
                            <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ target_pct: 100, time_s: 60, repeats: 1 })}>
                                Добавить интервал
                            </Button>
                        </>
                    )}
                </Form.List>
            </Form>
        </Modal>
    )
}

function ViewWorkouts({ onStartWorkout }) {
    const dispatch = useDispatch()
    const workouts = useSelector(state => state.workouts.workouts)
    const loading = useSelector(state => state.workouts.loading)

    const [searchText, setSearchText] = useState('')
    const [activeTab, setActiveTab] = useState('all')
    const [durationFilter, setDurationFilter] = useState(undefined)
    const [zoneFilter, setZoneFilter] = useState([])
    const [zoneMatch, setZoneMatch] = useState('all')
    const [minimumShare, setMinimumShare] = useState(20)
    const [sortKey, setSortKey] = useState('default')
    const [favoriteIds, setFavoriteIds] = useState(() => getLocalStorageValue(FAVORITES_KEY, []))
    const [selectedWorkout, setSelectedWorkoutLocal] = useState(undefined)
    const [designerOpen, setDesignerOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)

    useEffect(() => {
        requestWorkouts()
        checkDaemonCompatibility()
    }, [])

    useEffect(() => {
        setLocalStorageValue(FAVORITES_KEY, favoriteIds)
    }, [favoriteIds])

    const visibleWorkouts = useMemo(() => {
        const filtered = filterWorkouts(workouts, {
            searchText,
            tab: activeTab,
            durationFilter,
            zoneFilter,
            zoneMatch,
            minimumShare,
            favoriteIds,
        })
        const zoneKey = zoneFilter && zoneFilter.length ? zoneFilter[0] : 'threshold'
        return sortWorkouts(filtered, { sortKey, zoneKey })
    }, [workouts, searchText, activeTab, durationFilter, zoneFilter, zoneMatch, minimumShare, sortKey, favoriteIds])

    const openPlayer = async workout => {
        const verdict = await assertDaemonCompatible()
        if (!verdict.ok) {
            showDaemonBlockModal(verdict)
            return
        }
        const ftp = getLocalStorageValue(FTP_KEY, 200) || 200
        onStartWorkout(workout, ftp)
    }

    const selectWorkout = workout => {
        setSelectedWorkoutLocal(workout)
        dispatch(setSelectedWorkout(workout))
    }

    const clearFilters = () => {
        setSearchText('')
        setDurationFilter(undefined)
        setZoneFilter([])
        setZoneMatch('all')
        setMinimumShare(20)
        setSortKey('default')
    }

    const hasFilters = searchText || durationFilter || (zoneFilter && zoneFilter.length) || sortKey !== 'default'

    const toolbar = (
        <>
            <Input.Search
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Поиск по названию или автору"
                allowClear
            />
            <Select value={durationFilter} onChange={setDurationFilter} allowClear
                placeholder="Длительность" options={DURATION_OPTIONS} />
            <Select mode="multiple" value={zoneFilter} onChange={setZoneFilter} allowClear
                placeholder="Зоны" options={ZONE_OPTIONS} maxTagCount="responsive" />
            <Select value={zoneMatch} onChange={setZoneMatch}
                options={[{ value: 'all', label: 'Все выбранные зоны' }, { value: 'any', label: 'Любая выбранная зона' }]} />
            <InputNumber min={0} max={100} value={minimumShare} onChange={setMinimumShare}
                addonAfter="%" placeholder="мин. доля" />
            <Select value={sortKey} onChange={setSortKey} options={SORT_OPTIONS} />
            <Select disabled placeholder="Сложность" style={{ width: '100%' }} />
            <Button onClick={clearFilters}>Сбросить</Button>
        </>
    )

    return (
        <main className="workouts-catalogue">
            <div className="workouts-catalogue__inner">
                <nav className="workouts-tabs" aria-label="Каталог тренировок">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            type="button"
                            className={tab.key === activeTab ? 'is-active' : ''}
                            aria-pressed={tab.key === activeTab}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>

                <header className="workouts-heading">
                    <div>
                        <p className="workouts-eyebrow">ТРЕНИРОВКИ</p>
                        <h1>Все тренировки</h1>
                    </div>
                    <Button type="primary" onClick={() => setDesignerOpen(true)}>Создать тренировку</Button>
                </header>

                <section className="workouts-toolbar" aria-label="Фильтры тренировок">
                    {toolbar}
                    <span className="workouts-toolbar__mobile-filters">
                        <Button onClick={() => setDrawerOpen(true)}>Фильтры</Button>
                    </span>
                </section>

                <div className="workouts-list-header">
                    <span>Показано {visibleWorkouts.length} из {(workouts || []).length} тренировок</span>
                    {loading && <span aria-live="polite">Загрузка...</span>}
                    {hasFilters && <button type="button" className="workouts-clear-filters" onClick={clearFilters}>Сбросить фильтры</button>}
                </div>

                <section className="workouts-list" aria-label="Список тренировок">
                    {(visibleWorkouts || []).map(workout => (
                        <article className="workout-card" key={workout.id}>
                            <button className="workout-card__select" type="button"
                                onClick={() => selectWorkout(workout)}>
                                <WorkoutProfileThumbnail points={workout.points} />
                                <div className="workout-card__body">
                                    <h2>{workout.name || 'Без названия'}</h2>
                                    <p className="workout-card__author">{workout.author || 'Автор не указан'}</p>
                                    <dl className="workout-card__metrics">
                                        <div><dt>Время</dt><dd>{formatDuration(workoutTotalSeconds(workout))}</dd></div>
                                        <div><dt>Зоны</dt><dd>{formatZoneSummary(workout)}</dd></div>
                                    </dl>
                                </div>
                            </button>
                            <div className="workout-card__actions">
                                <Button type="text"
                                    aria-pressed={favoriteIds.includes(workout.id)}
                                    aria-label={favoriteIds.includes(workout.id) ? 'Удалить из избранного' : 'Добавить в избранное'}
                                    onClick={() => setFavoriteIds(ids => toggleFavoriteIds(ids, workout.id))}>
                                    {favoriteIds.includes(workout.id) ? '★' : '☆'}
                                </Button>
                                <Button type="primary" onClick={() => openPlayer(workout)}>Начать</Button>
                            </div>
                        </article>
                    ))}
                    {!loading && (visibleWorkouts || []).length === 0 && <Empty description="Тренировки не найдены" />}
                </section>

                {selectedWorkout && <WorkoutPreview workout={selectedWorkout} onStart={openPlayer} />}
            </div>

            <Drawer title="Фильтры" placement="right" width={320} visible={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <div className="workouts-drawer-filters">
                    <Input.Search
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        placeholder="Поиск по названию или автору"
                        allowClear
                    />
                    <Select value={durationFilter} onChange={setDurationFilter} allowClear placeholder="Длительность" options={DURATION_OPTIONS} style={{ width: '100%' }} />
                    <Select mode="multiple" value={zoneFilter} onChange={setZoneFilter} allowClear placeholder="Зоны" options={ZONE_OPTIONS} style={{ width: '100%' }} />
                    <Select value={zoneMatch} onChange={setZoneMatch} options={[{ value: 'all', label: 'Все выбранные зоны' }, { value: 'any', label: 'Любая выбранная зона' }]} style={{ width: '100%' }} />
                    <InputNumber min={0} max={100} value={minimumShare} onChange={setMinimumShare} addonAfter="%" style={{ width: '100%' }} />
                    <Select value={sortKey} onChange={setSortKey} options={SORT_OPTIONS} style={{ width: '100%' }} />
                    <Button block onClick={() => { clearFilters(); setDrawerOpen(false) }}>Сбросить</Button>
                </div>
            </Drawer>

            <DesignerModal open={designerOpen} onClose={() => setDesignerOpen(false)} onCreated={() => requestWorkouts()} />
        </main>
    )
}

export default ViewWorkouts
